import { CarouselAnalyticsComponentService, ProductListAnalyticsService } from '@analytics/feature';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { ProductListService } from '@catalog/feature';
import { BaseStoreFacade } from '@city/data-access';
import { CustomProduct, CustomProductScope, ProductGridItem } from '@product/const';
import { ProductCodes, ProductRecommendationCarouselTypes, ProductRecommendationEntity } from '@product/const/v2';
import { ProductRecommendationService } from '@product/data-access/v2';
import { RetailRocketCarouselStateService, RetailRocketProductReferenceService } from '@reference/feature';
import { InViewportDirective } from '@sdvor/shared/ui';
import { SdvorSwiperComponent } from '@sdvor/shared/ui/v2';
import { DeviceService } from '@shared/services';
import { BREAKPOINT } from '@spartacus/storefront';
import {
  asyncScheduler,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  observeOn,
  of,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';
import { Swiper, SwiperOptions } from 'swiper/types';
import { SdvorProductItemComponent } from '../product-item';

@Component({
  selector: 'sd-product-carousel',
  imports: [AsyncPipe, InViewportDirective, SdvorSwiperComponent, SdvorProductItemComponent],
  templateUrl: './product-carousel.component.html',
  styleUrls: ['./product-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    CarouselAnalyticsComponentService,
    RetailRocketCarouselStateService,
    {
      provide: ProductListAnalyticsService,
      useExisting: CarouselAnalyticsComponentService,
    },
  ],
})
export class ProductCarouselComponent implements OnInit, OnDestroy {
  position = input.required<number>();
  productIdsContext = input<string[]>([]);

  protected readonly breakpoint = toSignal(this.deviceService.breakpoint$, {
    initialValue: BREAKPOINT.xxs,
  });

  protected readonly swiperConfig: SwiperOptions = {
    slidesPerView: 2,
    slidesPerGroup: 2,
    spaceBetween: 4,
    scrollbar: { enabled: false },
    navigation: { enabled: false },
    breakpoints: {
      [this.deviceService.sizes.sm]: {
        slidesPerView: 3,
        slidesPerGroup: 3,
      },
      [this.deviceService.sizes.md]: {
        spaceBetween: 20,
        slidesPerView: 5,
        slidesPerGroup: 5,
        navigation: { enabled: true },
      },
    },
  };

  protected readonly skeletonSlidesPerView = signal(0);

  protected carousel$: Observable<ProductRecommendationEntity | undefined>;
  protected products$: Observable<ProductGridItem[]>;
  protected isLoading$: Observable<boolean>;

  private readonly path = signal(
    '/' +
      (this.activatedRoute.snapshot.pathFromRoot
        .flatMap((r) => r.url)
        .map((s) => s.path)
        .join('/') || 'homepage'),
  );
  private readonly carouselUid = computed(() => `${this.path()}_${this.position()}`);
  private readonly productIdsContext$ = toObservable(this.productIdsContext).pipe(
    distinctUntilChanged((a, b) => a.toString() === b.toString()),
  );
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private deviceService: DeviceService,
    private activatedRoute: ActivatedRoute,
    private baseStoreFacade: BaseStoreFacade,
    private productListService: ProductListService,
    private productRecommendationService: ProductRecommendationService,
    private retailRocketProductReferenceService: RetailRocketProductReferenceService,
    private carouselAnalyticsComponentService: CarouselAnalyticsComponentService,
    private retailRocketCarouselStateService: RetailRocketCarouselStateService,
  ) {}

  ngOnInit(): void {
    this.baseStoreFacade
      .getActiveCode()
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((baseStore) => {
        this.productRecommendationService.loadProductRecommendation(baseStore);
      });

    this.isLoading$ = combineLatest({
      productRecommendationLoading: this.productRecommendationService.getProductRecommendationLoading(),
      productListLoading: this.productListService.getLoading(),
      retailRocketRecomendationsLoading: this.retailRocketProductReferenceService.getRetailRocketRecomendationsLoading(
        this.carouselUid(),
      ),
    }).pipe(
      map(
        ({ productRecommendationLoading, productListLoading, retailRocketRecomendationsLoading }) =>
          productRecommendationLoading || productListLoading || retailRocketRecomendationsLoading,
      ),
    );

    this.carousel$ = this.productRecommendationService.getProductRecommendation().pipe(
      map((carousels) => {
        return carousels
          .filter((carousel) => !!carousel.page)
          .find(
            (carousel) =>
              new RegExp(carousel.page.route).test(this.path()) && carousel.page.position === this.position(),
          );
      }),
      distinctUntilChanged(
        (a, b) =>
          a?.title === b?.title &&
          a?.page.position === b?.page.position &&
          a?.page.route === b?.page.route &&
          a?.carouselType === b?.carouselType,
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.products$ = combineLatest({
      carousel: this.carousel$,
      productIdsContext: this.productIdsContext$,
      baseStore: this.baseStoreFacade.getActiveCode().pipe(distinctUntilChanged(), observeOn(asyncScheduler)),
    }).pipe(
      filter(({ carousel }) => {
        if (!carousel) return false;

        switch (carousel.carouselType) {
          case ProductRecommendationCarouselTypes.PRODUCT_LIST:
            return !!carousel.productCodes?.length;
          case ProductRecommendationCarouselTypes.ALGORITHM_LIST:
            return !!carousel.algorithms?.length;
          case ProductRecommendationCarouselTypes.COMBINED:
            return !!carousel.productCodes?.length || !!carousel.algorithms?.length;
          default:
            return false;
        }
      }),
      distinctUntilChanged(
        (a, b) =>
          a.baseStore === b.baseStore &&
          a.productIdsContext.toString() === b.productIdsContext.toString() &&
          a.carousel?.title === b.carousel?.title &&
          a.carousel?.carouselType === b.carousel?.carouselType &&
          a.carousel?.page?.position === b.carousel?.page?.position,
      ),
      switchMap(({ carousel, productIdsContext }) => {
        if (!carousel) return of([]);

        const limit = carousel.itemLimits;
        let products$: Observable<ProductGridItem[]>;

        switch (carousel.carouselType) {
          case ProductRecommendationCarouselTypes.PRODUCT_LIST:
            products$ = this.handleProductList(carousel.productCodes || [], limit);
            break;
          case ProductRecommendationCarouselTypes.ALGORITHM_LIST:
            products$ = this.handleAlgorithmList(carousel, productIdsContext, limit);
            break;
          case ProductRecommendationCarouselTypes.COMBINED:
            products$ = this.handleCombinedList(carousel, productIdsContext, limit);
            break;
          default:
            products$ = of([]);
        }

        return products$.pipe(startWith([]));
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    combineLatest({ carousel: this.carousel$, productIdsContext: this.productIdsContext$ })
      .pipe(
        filter(
          (ctx): ctx is { carousel: ProductRecommendationEntity; productIdsContext: string[] } =>
            !!ctx.carousel && !!ctx.carousel.page?.route,
        ),
        distinctUntilChanged(
          (a, b) =>
            a.carousel.title === b.carousel.title &&
            a.carousel.page.position === b.carousel.page.position &&
            a.carousel.page.route === b.carousel.page.route &&
            a.productIdsContext.toString() === b.productIdsContext.toString(),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ carousel }) => {
        const page = carousel.page.route.slice(1);
        const carouselName = carousel.title;
        const carouselType = carousel.carouselType;
        const algorithms = carousel?.algorithms ? carousel.algorithms.map((alg) => alg.code).join(',') : '';

        this.carouselAnalyticsComponentService.init(
          `${carouselType}_${page}_${carouselName}_${algorithms}`,
          this.products$ as Observable<CustomProduct[]>,
        );
      });
  }

  ngOnDestroy(): void {
    this.retailRocketProductReferenceService.resetRetailRocketRecomendationList(this.carouselUid());
  }

  onSwiperInit(swiper: Swiper): void {
    const slidesPerView = typeof swiper?.params.slidesPerView === 'number' ? swiper.params.slidesPerView : 0;
    this.skeletonSlidesPerView.set(slidesPerView);
  }

  onSwiperActiveIndexChange(index: number): void {
    this.carouselAnalyticsComponentService.setOffset(index);
  }

  onSwiperSlidesPerViewChange(slidesPerView: number): void {
    this.skeletonSlidesPerView.set(slidesPerView);
    this.carouselAnalyticsComponentService.setSlidesPerView(slidesPerView);
  }

  onCarouselViewportIntersect(): void {
    this.carouselAnalyticsComponentService.setIntersected();
  }

  private handleProductList(codes: ProductCodes[], limit: number | undefined): Observable<ProductGridItem[]> {
    const productCodes = codes.map((pc) => pc.code).slice(0, limit);

    this.productListService.loadProductsByCodes(productCodes, CustomProductScope.REFERENCE);

    return this.productListService.getProductsByCodes(productCodes, CustomProductScope.REFERENCE);
  }

  private handleAlgorithmList(
    carousel: ProductRecommendationEntity,
    productIdsContext: string[],
    limit: number | undefined,
  ): Observable<ProductGridItem[]> {
    if (!carousel.algorithms || carousel.algorithms.length === 0) return of([]);

    const algorithms = carousel.algorithms.map((alg) => alg.code).join(',');
    const uid = this.carouselUid();
    this.retailRocketCarouselStateService.init(carousel.algorithms[0].code);

    this.retailRocketProductReferenceService.loadRetailRocketRecomendations(uid, productIdsContext, algorithms);

    return this.retailRocketProductReferenceService.getRetailRocketRecomendationsIdsState(uid).pipe(
      filter((state) => !state.loading && !!state.success),
      switchMap((state) => {
        const codes = state.value;
        if (!codes?.length) return of([]);

        const limitedCodes = codes.map((c) => c.toString()).slice(0, limit);

        return this.productListService.getProductsByCodes(limitedCodes, CustomProductScope.REFERENCE);
      }),
    );
  }

  private handleCombinedList(
    carousel: ProductRecommendationEntity,
    productIdsContext: string[],
    limit: number | undefined,
  ): Observable<ProductGridItem[]> {
    if (carousel.algorithms && carousel.algorithms.length > 0) {
      this.retailRocketCarouselStateService.init(carousel.algorithms[0].code);
    }

    const productCodes = carousel.productCodes?.map((pc) => pc.code) || [];

    if (productCodes.length) {
      this.productListService.loadProductsByCodes(productCodes, CustomProductScope.REFERENCE);
    }

    const products$ = productCodes.length
      ? this.productListService.getProductsByCodes(productCodes, CustomProductScope.REFERENCE)
      : of([]);

    const recommendations$ = this.handleAlgorithmList(carousel, productIdsContext, limit);

    return combineLatest({
      products: products$,
      recommendations: recommendations$,
    }).pipe(
      map(({ products, recommendations }) => {
        const combined = [...products, ...recommendations];

        return combined.slice(0, limit);
      }),
    );
  }
}
